import React from "react";
import { Form, Select, Input, InputNumber, DatePicker, Row, Col, Typography, Badge } from "antd";
import { PictureOutlined, VideoCameraOutlined, SettingOutlined } from "@ant-design/icons";
import { noWhitespaceValidator } from "../../utils/commonFunctions";

const { Title } = Typography;

const { Option } = Select;

const BannerForm = ({
  form,
  videoShows,
  tvShows,
  onBannerTypeChange,
  onShowTypeChange,
  children,
}) => {
  const antdForm = Form.useFormInstance();
  const bannerType =
    Form.useWatch("m_banner_type", antdForm) ?? form?.m_banner_type;

  // generic/promotion carry a redirect URL instead of a show.
  const isLinkBanner = ["generic", "promotion"].includes(bannerType);

  return (
    <div className="space-y-6">
      <div
        className="bg-white p-6 rounded-lg border border-gray-200"
        style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <PictureOutlined />
          <Title
            level={4}
            className="!mb-0"
            style={{ color: "rgba(0, 0, 0, 0.88)" }}
          >
            Basic Information
          </Title>
        </div>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Name
                </span>
              }
              name="banner_name"
              rules={[
                { required: true, message: "Please enter banner name" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <Input placeholder="Enter banner name" className="rounded-lg" />
            </Form.Item>
          </Col>

          {/* Drawn over the sleek banner card's artwork; other templates ignore
              it. Separate from Banner Name, the CMS's own label. */}
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Title
                </span>
              }
              name="banner_title"
            >
              <Input
                placeholder="e.g. Midnight Premiere: Shadow Wire"
                className="rounded-lg"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Date
                </span>
              }
              name="banner_date"
            >
              {/* Column is a DATE, so the picker guarantees the API gets a
                  parseable value. DD-MM-YYYY matches how the collection API
                  serves it back. */}
              <DatePicker
                format="DD-MM-YYYY"
                placeholder="Select date"
                className="rounded-lg w-full"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Subtitle
                </span>
              }
              name="banner_subtitle"
            >
              <Input
                placeholder="e.g. All Locations"
                className="rounded-lg"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Status
                </span>
              }
              name="banner_is_active"
              rules={[
                { required: true, message: "Please select banner status" },
              ]}
            >
              <Select placeholder="Select status" className="rounded-lg">
                <Option value="Y">
                  <Badge status="success" text="Active" />
                </Option>
                <Option value="N">
                  <Badge status="default" text="Inactive" />
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </div>


      <div
        className="bg-white p-6 rounded-lg border border-gray-200"
        style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <VideoCameraOutlined />
          <Title
            level={4}
            className="!mb-0"
            style={{ color: "rgba(0, 0, 0, 0.88)" }}
          >
            Banner Type & Content
          </Title>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <Form.Item
            label={
              <span
                className="font-semibold"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Banner Type
              </span>
            }
            name="m_banner_type"
            rules={[{ required: true, message: "Please select banner type" }]}
          >
            <Select
              placeholder="Select banner type"
              onChange={onBannerTypeChange}
              className="rounded-lg"
              showSearch
              filterOption={(input, option) =>
                option.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="generic">Generic</Option>
              <Option value="show"> Show</Option>
              <Option value="promotion"> Promotion</Option>
            </Select>
          </Form.Item>

          {/* The banner's single redirect link lives in Configuration, below. */}

          {form.m_banner_type === "show" && (
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Show Type
                </span>
              }
              name="show_type"
              rules={[{ required: true, message: "Please select show type" }]}
            >
              <Select
                placeholder="Select show type"
                onChange={onShowTypeChange}
                className="rounded-lg"
                showSearch
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="movie">Movie</Option>
                <Option value="series">Series</Option>
              </Select>
            </Form.Item>
          )}

          {form.m_banner_type === "show" && form.show_type && (
            <Form.Item
              label={
                <span
                  className="font-semibold"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Select {form.show_type === "movie" ? "Movie" : "Series"}
                </span>
              }
              name="show_id"
              rules={[
                {
                  required: true,
                  message: `Please select a ${form.show_type}`,
                },
              ]}
            >
              <Select
                placeholder={`Select ${form.show_type}`}
                className="rounded-lg"
                showSearch
                optionFilterProp="children"
              >
                {form.show_type === "movie"
                  ? (Array.isArray(videoShows)
                      ? videoShows
                      : videoShows?.data || []
                    ).map((show) => (
                      <Option
                        key={show.video_show_id}
                        value={show.video_show_id}
                      >
                        {show.video_show_title}
                      </Option>
                    ))
                  : (Array.isArray(tvShows)
                      ? tvShows
                      : tvShows?.data || []
                    ).map((show) => (
                      <Option key={show.tv_show_id} value={show.tv_show_id}>
                        {show.tv_show_name}
                      </Option>
                    ))}
              </Select>
            </Form.Item>
          )}

          {/* The rail this banner belongs to. Offered for every banner type: a
              rail is independent of what the banner does on click. Optional,
              and independent of Content ID — no variant means no rail. */}
          <Form.Item
            label={
              <span
                className="font-semibold"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Content Variant
              </span>
            }
            name="content_variant"
          >
            <Select
              allowClear
              placeholder="Select content variant"
              className="rounded-lg"
              showSearch
              optionFilterProp="children"
            >
              {/* The values are the enum's own; the labels are only display. */}
              <Option value="COMING_SOON">Coming Soon</Option>
              <Option value="NOW_SHOWING">Now Showing</Option>
              <Option value="DRAMA">Drama</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <span
                className="font-semibold"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Content ID
              </span>
            }
            name="content_id"
          >
            {/* The column is an integer: precision={0} stops a decimal reaching
                the API to be silently truncated there. */}
            <InputNumber
              min={1}
              precision={0}
              placeholder="e.g. 101"
              className="rounded-lg"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* No banner-level Language picker, deliberately: language lives on
              the per-language image sets in Media Assets, and nothing reads
              m_banner.lang_id. */}
        </div>
      </div>

      <div
        className="bg-white p-6 rounded-lg border border-gray-200"
        style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <SettingOutlined />
          <Title
            level={4}
            className="!mb-0"
            style={{ color: "rgba(0, 0, 0, 0.88)" }}
          >
            Configuration
          </Title>
        </div>

        <Row gutter={[24, 16]}>

          {/* Only link banners get one — a show banner opens its show instead. */}
          {isLinkBanner && (
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                label={
                  <span
                    className="font-semibold"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Redirect URL
                  </span>
                }
                name="redirect_url"
                extra="Optional. Clicking the banner opens this link; left empty, the banner runs as artwork only."
                rules={[
                  // noWhitespaceValidator is deliberately not used here: it
                  // rejects an empty value, and this field is optional. Only a
                  // URL that IS given is checked for shape.
                  {
                    validator: (_, value) => {
                      if (!value || String(value).trim() === "") {
                        return Promise.resolve();
                      }
                      try {
                        const parsed = new URL(String(value).trim());
                        return ["http:", "https:"].includes(parsed.protocol)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "URL must start with http:// or https://"
                              )
                            );
                      } catch {
                        return Promise.reject(
                          new Error(
                            "Enter a valid URL, e.g. https://example.com"
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder="https://example.com/offer"
                  className="rounded-lg"
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </div>

      {children && (
        <div
          className="bg-white p-6 rounded-lg border border-gray-200"
          style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
        >
          <div className="flex items-center gap-2 mb-4">
            <PictureOutlined />
            <Title
              level={4}
              className="!mb-0"
              style={{ color: "rgba(0, 0, 0, 0.88)" }}
            >
              Media Assets
            </Title>
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

export default BannerForm;
